import React from "react";
import { ToggleControl, TextControl, Button, Spinner, Notice } from "@wordpress/components";
import { useState, useEffect } from "@wordpress/element";
import { __ } from "@wordpress/i18n";
import { getImageHubSettings } from "../context/useSettings"

const ajaxUrl = image_hub_data.ajaxurl;
const nonce = image_hub_data.nonce;


const DEFAULT_STATE = {
	unsplash_access_key: "",
	pexels_api_key: "",
	giphy_api_key: "",
	pixabay_api_key: "",
	openverse_client_id: "",
	openverse_client_secret: "",
	image_hub_use_default_keys: false
}

export const ApiKeysMgr = () => {
	// Initial state setup
	const [apiKeys, setApiKeys] = useState(DEFAULT_STATE);
	const [originalApikeys, setOAK] = useState(DEFAULT_STATE)
	const [initiated, setInitiated] = useState(false)
	const hasApiKeysChanged = () => {
		return Object.keys(DEFAULT_STATE).some(key => apiKeys[key] !== originalApikeys[key]);
	};

	const [visibleFields, setVisibleFields] = useState({
		unsplash_access_key: false,
		pexels_api_key: false,
		giphy_api_key: false,
		pixabay_api_key: false,
		openverse_client_id: false,
		openverse_client_secret: false,
	});

	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [saveMessage, setSaveMessage] = useState("");
	const [saveStatus, setSaveStatus] = useState("");
	const settings = getImageHubSettings()

	window.originalApikeys = originalApikeys
	window.apiKeys = apiKeys
	useEffect(() => {
		if (!initiated) {
			setInitiated(true)
			setIsLoading(settings.isLoading)
			setOAK(settings.apiKeys)
			setApiKeys(settings.apiKeys);
		}

	}, [settings]);

	// Function to fetch existing API keys from WordPress

	// Handle input field changes
	const handleChange = (key, value) => {
		setApiKeys((prevState) => ({
			...prevState,
			[key]: value,
		}));
	};

	// Save API keys to WordPress
	const saveAPIKeys = () => {
		setIsSaving(true);

		// Prepare data for the WordPress REST API
		const data = {};
		Object.entries(apiKeys).forEach(([key, value]) => {
			if (key.includes('default')) {
				data[key] = value
			} else {
				data[`image_hub_api_keys_${key}`] = value;
			}
		});

		fetch(ajaxUrl, {
			method: "POST",
			body: new URLSearchParams({
				action: "image_hub_update_settings",
				nonce: nonce,
				data: JSON.stringify(data)
			}),
		}).then((response) => {
			setIsSaving(false);
			setSaveMessage(__("API keys saved successfully!", "image-hub"));
			setSaveStatus("success");
			setOAK(apiKeys)
			// Clear success message after 3 seconds
			setTimeout(() => {
				setSaveMessage("");
				setSaveStatus("");
			}, 3000);
		})
			.catch((error) => {
				console.error("Error saving API keys:", error);
				setIsSaving(false);
				setSaveMessage(__("Failed to save API keys.", "image-hub"));
				setSaveStatus("error");
			});
	};

	const toggleFieldVisibility = (key) => {
		setVisibleFields((prevState) => ({
			...prevState,
			[key]: !prevState[key],
		}));
	};

	// Show loading spinner while loading
	if (isLoading) {
		return (
			<div className="image-hub-api-keys-manager-loading">
				<Spinner />
				<p>{__("Loading API keys...", "image-hub")}</p>
			</div>
		);
	}

	return (
		<div>
			<div className="image-hub-image-hub-api-key-field-container">
				<ToggleControl
					__nextHasNoMarginBottom

					label={__('Use default API keys provided by the plugin', "image-hub")}
					help={() => <div>
						{__('The plugin includes built-in API keys, but you can replace them with your own.')}<br/>
						{__('(Some API\'s don\'t use api key for fetching without search param)')}
					</div>}
					checked={apiKeys.image_hub_use_default_keys}
					onChange={() =>
						handleChange('image_hub_use_default_keys', !apiKeys.image_hub_use_default_keys)
					}
				/>
			</div>
			<br />
			<br />
			<div className="image-hub-image-hub-api-key-field-container">
				<TextControl
					__nextHasNoMarginBottom
					__next40pxDefaultSize
					label={__("Unsplash Access Key", "image-hub")}
					value={apiKeys.unsplash_access_key}
					onChange={(value) =>
						handleChange("unsplash_access_key", value)
					}
					autoComplete="new-password"
					className="image-hub-api-key-field"
					type={
						visibleFields.unsplash_access_key ? "text" : "password"
					}
				/>
				<Button
					__next40pxDefaultSize
					variant="secondary"
					onClick={() => toggleFieldVisibility("unsplash_access_key")}
					style={{ color: "#3858e9" }}
					icon={
						visibleFields.unsplash_access_key
							? "visibility"
							: "hidden"
					}
					label={
						visibleFields.unsplash_access_key
							? __("Hide", "image-hub")
							: __("Show", "image-hub")
					}
				/>
			</div>
			<a target="_blank" href="https://unsplash.com/developers">
				Get API Key
			</a>
			<div className="image-hub-image-hub-api-key-field-container">
				<TextControl
					__nextHasNoMarginBottom
					__next40pxDefaultSize
					label={__("Pexels API Key", "image-hub")}
					value={apiKeys.pexels_api_key}
					onChange={(value) => handleChange("pexels_api_key", value)}
					className="image-hub-api-key-field"
					type={visibleFields.pexels_api_key ? "text" : "password"}
					autoComplete="new-password"
				/>
				<Button
					__next40pxDefaultSize
					variant="secondary"
					style={{ color: "#3858e9" }}
					onClick={() => toggleFieldVisibility("pexels_api_key")}
					icon={
						visibleFields.pexels_api_key ? "visibility" : "hidden"
					}
					label={
						visibleFields.pexels_api_key
							? __("Hide", "image-hub")
							: __("Show", "image-hub")
					}
				/>
			</div>
			<a
				target="_blank"
				href="https://help.pexels.com/hc/en-us/articles/900004904026-How-do-I-get-an-API-key"
			>
				Get API Key
			</a>
			<div className="image-hub-image-hub-api-key-field-container">
				<TextControl
					__nextHasNoMarginBottom
					__next40pxDefaultSize
					label={__("Giphy API Key", "image-hub")}
					value={apiKeys.giphy_api_key}
					onChange={(value) => handleChange("giphy_api_key", value)}
					className="image-hub-api-key-field"
					type={visibleFields.giphy_api_key ? "text" : "password"}
					autoComplete="new-password"
				/>
				<Button
					__next40pxDefaultSize
					variant="secondary"
					style={{ color: "#3858e9" }}
					onClick={() => toggleFieldVisibility("giphy_api_key")}
					icon={visibleFields.giphy_api_key ? "visibility" : "hidden"}
					label={
						visibleFields.giphy_api_key
							? __("Hide", "image-hub")
							: __("Show", "image-hub")
					}
				/>
			</div>
			<a target="_blank" href="https://developers.giphy.com/docs/api/">
				Get API Key
			</a>
			<div className="image-hub-image-hub-api-key-field-container">
				<TextControl
					__nextHasNoMarginBottom
					__next40pxDefaultSize
					label={__("Pixabay API Key", "image-hub")}
					value={apiKeys.pixabay_api_key}
					onChange={(value) => handleChange("pixabay_api_key", value)}
					className="image-hub-api-key-field"
					type={visibleFields.pixabay_api_key ? "text" : "password"}
					autoComplete="new-password"
				/>
				<Button
					__next40pxDefaultSize
					variant="secondary"
					style={{ color: "#3858e9" }}
					onClick={() => toggleFieldVisibility("pixabay_api_key")}
					icon={
						visibleFields.pixabay_api_key ? "visibility" : "hidden"
					}
					label={
						visibleFields.pixabay_api_key
							? __("Hide", "image-hub")
							: __("Show", "image-hub")
					}
				/>
			</div>
			<a target="_blank" href="https://pixabay.com/service/about/api/">
				Get API Key
			</a>
			<div className="image-hub-image-hub-api-key-field-container">
				<TextControl
					__nextHasNoMarginBottom
					__next40pxDefaultSize
					label={__("Openverse Client ID", "image-hub")}
					value={apiKeys.openverse_client_id}
					onChange={(value) =>
						handleChange("openverse_client_id", value)
					}
					className="image-hub-api-key-field"
					type={
						visibleFields.openverse_client_id ? "text" : "password"
					}
					autoComplete="new-password"
				/>
				<Button
					__next40pxDefaultSize
					variant="secondary"
					style={{ color: "#3858e9" }}
					onClick={() => toggleFieldVisibility("openverse_client_id")}
					icon={
						visibleFields.openverse_client_id
							? "visibility"
							: "hidden"
					}
					label={
						visibleFields.openverse_client_id
							? __("Hide", "image-hub")
							: __("Show", "image-hub")
					}
				/>
			</div>

			<div className="image-hub-image-hub-api-key-field-container">
				<TextControl
					__nextHasNoMarginBottom
					__next40pxDefaultSize
					label={__("Openverse Client Secret", "image-hub")}
					value={apiKeys.openverse_client_secret}
					onChange={(value) =>
						handleChange("openverse_client_secret", value)
					}
					className="image-hub-api-key-field"
					type={
						visibleFields.openverse_client_secret
							? "text"
							: "password"
					}
					autoComplete="new-password"
				/>
				<Button
					__next40pxDefaultSize
					variant="secondary"
					style={{ color: "#3858e9" }}
					onClick={() =>
						toggleFieldVisibility("openverse_client_secret")
					}
					icon={
						visibleFields.openverse_client_secret
							? "visibility"
							: "hidden"
					}
					label={
						visibleFields.openverse_client_secret
							? __("Hide", "image-hub")
							: __("Show", "image-hub")
					}
				/>
			</div>
			<div>
				<a
					target="_blank"
					href="https://api.openverse.org/v1/#tag/auth/operation/register"
				>
					Get API Key
				</a>
			</div>
			<div style={{ display: "flex", gap: "10px", alignItems: "baseline", justifyContent: "space-between" }}>
				<Button
					style={{ marginTop: "16px", flex: "0" }}
					variant="primary"
					onClick={saveAPIKeys}
					isBusy={isSaving}
					disabled={isSaving || !hasApiKeysChanged()}
				>
					{isSaving
						? __("Saving...", "image-hub")
						: __("Save API Keys", "image-hub")}
				</Button>
				<div style={{ width: "90%" }}>
					{saveMessage && (
						<Notice status={saveStatus} isDismissible={false}>
							{saveMessage}
						</Notice>
					)}
				</div>
			</div>
		</div>
	);
};
